// Playoffs of a Swiss / group stage (two-stage bracket): single or double elimination, seeded
// from the standings and built when the stage ends (PUT /api/brackets/:id/playoffs).
// PlayoffFields = the settings block, also used by the create dialog.
import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../AnimatedModal';
import './bracket.css';

export const DEFAULT_PLAYOFFS = { bracket_type: 'single_elimination', advance: 2, has_third_place: false, grand_final_mode: 'simple' };

/** How many teams reach the playoffs: groups = top `advance` of each group, Swiss = top `advance`. */
export function playoffTeams(cfg, { isGroups, groupSizes = [], teamCount = 0 }) {
  if (!cfg) return 0;
  if (isGroups) return groupSizes.reduce((s, n) => s + Math.min(cfg.advance, n), 0);
  return Math.min(cfg.advance, teamCount);
}

export function PlayoffFields({ value, onChange, isGroups, groupSizes, teamCount, locked }) {
  const { t } = useTranslation();
  const on = !!value;
  const cfg = value || DEFAULT_PLAYOFFS;
  const set = (patch) => onChange({ ...cfg, ...patch });
  const maxAdvance = isGroups ? Math.max(1, ...(groupSizes || [1])) : Math.max(2, teamCount || 2);
  const total = playoffTeams(cfg, { isGroups, groupSizes, teamCount });

  return (
    <div className="br-field">
      <label className="br-row" style={{ cursor: locked ? 'default' : 'pointer', gap: '0.6rem' }}>
        <button type="button" className={`br-toggle${on ? ' on' : ''}`} disabled={locked}
          onClick={() => onChange(on ? null : { ...DEFAULT_PLAYOFFS, advance: isGroups ? 2 : Math.min(8, Math.max(2, teamCount || 2)) })}><span /></button>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{t('bracket.po.toggle', 'Playoffs after this stage')}</span>
      </label>
      {on && (
        <div className="br-form" style={{ marginTop: '0.5rem', gap: '0.55rem' }}>
          <div className="br-types" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            {[['single_elimination', t('bracket.type.single', 'Single elimination')], ['double_elimination', t('bracket.type.double', 'Double elimination')]].map(([k, h]) => (
              <button key={k} type="button" disabled={locked} className={`br-type${cfg.bracket_type === k ? ' on' : ''}`} onClick={() => set({ bracket_type: k })}><div className="h">{h}</div></button>
            ))}
          </div>
          <div className="br-row" style={{ flexWrap: 'wrap', gap: '0.6rem' }}>
            <label className="br-format">
              {isGroups ? t('bracket.playoffs.perGroup', 'Teams through per group') : t('bracket.playoffs.top', 'Top teams through')}
              <select value={cfg.advance} disabled={locked} onChange={e => set({ advance: Number(e.target.value) })}>
                {Array.from({ length: maxAdvance - (isGroups ? 0 : 1) }, (_, i) => i + (isGroups ? 1 : 2)).map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </label>
            {cfg.bracket_type === 'single_elimination' ? (
              <label className="br-format">
                {t('bracket.thirdPlaceMatch', '3rd place match')}
                <button type="button" className={`br-toggle${cfg.has_third_place ? ' on' : ''}`} disabled={locked || total < 4} onClick={() => set({ has_third_place: !cfg.has_third_place })}><span /></button>
              </label>
            ) : (
              <label className="br-format">
                {t('bracket.gf.label', 'Grand final')}
                <select value={cfg.grand_final_mode} disabled={locked} onChange={e => set({ grand_final_mode: e.target.value })}>
                  <option value="simple">{t('bracket.gf.simple', 'One match')}</option>
                  <option value="reset">{t('bracket.gf.reset', 'With reset')}</option>
                  <option value="none">{t('bracket.gf.none', 'None - upper final decides')}</option>
                </select>
              </label>
            )}
          </div>
          <div className="br-hint">
            {total >= 2 ? (isGroups
              ? t('bracket.po.groupsHint', '{{n}} teams go through. Group winners are the top seeds; teams from the same group meet as late as possible.', { n: total })
              : t('bracket.po.swissHint', '{{n}} teams go through, seeded by their Swiss standing.', { n: total }))
              : t('bracket.po.tooFew', 'At least 2 teams must go through.')}
            {' '}{t('bracket.po.when', 'The playoffs fill in from the standings when the stage ends - check the seeding, then start them.')}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PlayoffsModal({ isOpen, onClose, bracket, groups, config, onSaved }) {
  const { t } = useTranslation();
  const isGroups = bracket.bracket_type === 'group_stage';
  const [value, setValue] = useState(config || { ...DEFAULT_PLAYOFFS, advance: isGroups ? 2 : Math.min(8, bracket.team_count || 2) });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const groupSizes = (groups || []).map(g => (g.team_ids || []).length);
  const total = playoffTeams(value, { isGroups, groupSizes, teamCount: bracket.team_count });

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await axios.put(`/api/brackets/${bracket.id}/playoffs`, value || { bracket_type: null });
      onSaved();
    } catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <AnimatedModal isOpen={isOpen} onClose={onClose} title={t('bracket.po.title', 'Playoffs')} maxWidth="560px"
      footer={<>
        <button className="br-btn ghost" onClick={onClose} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
        <button className="br-btn" onClick={save} disabled={busy || (value && total < 2)}>{t('common.save', 'Save')}</button>
      </>}>
      <div className="br-form">
        <PlayoffFields value={value} onChange={setValue} isGroups={isGroups} groupSizes={groupSizes} teamCount={bracket.team_count} />
        <div className="br-hint">{t('bracket.po.rebuild', 'Playoffs that have not started are built again with these settings.')}</div>
        {err && <div className="br-err">{err}</div>}
      </div>
    </AnimatedModal>
  );
}
